// Turns a partner session payload (GET /sessions/:id) into the shape the
// result report and its PDF render. Pure — no React, no network.

import { displayRole } from "../partnerRoleCatalog";

export const BAND_ORDER = ["Strong", "Proficient", "Developing"];

export const BAND_MEANING = {
  Strong: "Clear, specific evidence of the behaviour the rubric looks for (75–100).",
  Proficient: "Some relevant evidence, with gaps in depth or specifics (50–74).",
  Developing: "Little or no evidence of the behaviour in the answer (0–49).",
};

export const FIT_GUIDANCE = {
  "Strong fit": "Clear evidence across most competencies. A good candidate to take to interview.",
  "Potential fit": "Evidence in some areas, gaps in others. Use the interview questions below to probe the gaps.",
  "Limited evidence":
    "The answers showed little evidence of the competencies this role needs. Weigh this alongside the CV, interview and references before deciding.",
};

export const ONE_INPUT_NOTE =
  "This assessment is one input to a hiring decision, not the decision. Use it alongside the CV, a structured interview and references.";

const AI_LEVELS = ["low", "medium", "high"];

export const fmtAway = (ms) => {
  const secs = Math.max(1, Math.round(ms / 1000));
  if (secs < 60) return `${secs}s`;
  const mins = Math.floor(secs / 60);
  const rest = secs % 60;
  return rest ? `${mins}m ${rest}s` : `${mins}m`;
};

// Partner invite tests only. The talent onboarding test uses a different,
// smaller set of signals and is not shown here.
export const partnerIntegrityLines = (signals) => {
  const lines = [];
  if (signals.pasted) lines.push(`${signals.pasted} paste attempt${signals.pasted === 1 ? "" : "s"}`);
  if (signals.tabs) {
    const away = signals.awayMs > 0 ? ` (${fmtAway(signals.awayMs)} in total)` : "";
    lines.push(`left the test page ${signals.tabs} time${signals.tabs === 1 ? "" : "s"}${away}`);
  }
  if (signals.fullscreen) lines.push(`${signals.fullscreen} fullscreen exit${signals.fullscreen === 1 ? "" : "s"}`);
  if (signals.devtools) lines.push("devtools opened");
  if (signals.rightClick) lines.push(`${signals.rightClick} right-click${signals.rightClick === 1 ? "" : "s"}`);
  if (signals.fast) lines.push(`${signals.fast} answer${signals.fast === 1 ? "" : "s"} typed too fast`);
  if (signals.duplicates) lines.push(`${signals.duplicates} similar answer${signals.duplicates === 1 ? "" : "s"}`);
  if (signals.aiLevel === "insufficient") lines.push("AI-likelihood: not enough text to judge");
  else if (AI_LEVELS.includes(signals.aiLevel)) lines.push(`AI-likelihood: ${signals.aiLevel}`);
  lines.push(signals.photo ? "photo captured" : "no photo");
  return lines;
};

export const fmtDateTime = (iso) => {
  if (!iso) return "just now";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return String(iso);
  return d.toLocaleString(undefined, { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
};

const bandFor = (score) => (score >= 75 ? "Strong" : score >= 50 ? "Proficient" : "Developing");

export function buildReport(d) {
  const test = d?.test_id && typeof d.test_id === "object" ? d.test_id : null;
  const rubricTest = d?.rubric_test_id && typeof d.rubric_test_id === "object" ? d.rubric_test_id : null;
  const r = d?.result || {};
  const sjt = d?.kind === "sjt" || !!r.sjt_integrity;

  const base = {
    sessionId: d?.session_id,
    cand: d?.candidate_name || d?.candidate_email || "Candidate",
    email: d?.candidate_email || "",
    role: displayRole(test?.role || rubricTest?.role || d?.test_role || ""),
    date: fmtDateTime(r.completed_at || d?.completed_at),
    timedOut: !!d?.timed_out,
    kind: sjt ? "sjt" : "mcq",
  };

  if (!sjt) {
    return {
      ...base,
      overall: r.percentage ?? 0,
      status: "scored",
      score: r.score ?? 0,
      totalQuestions: r.total_questions ?? 0,
      fakingRisk: r.integrity?.fakingRisk || "low",
    };
  }

  const integrity = r.sjt_integrity || {};
  const timings = Array.isArray(integrity.question_timings) ? integrity.question_timings : [];
  const questions = Array.isArray(d?.questions) ? d.questions : [];
  const answers = Array.isArray(d?.sjt_answers) ? d.sjt_answers : [];
  const answerFor = (competencyId) => {
    const q = questions.find((x) => x.competencyId === competencyId);
    const a = q ? answers.find((x) => x.question_id === q.id) : null;
    return { question: q?.scenario || "", answer: (a?.response || "").trim() };
  };

  const competencies = (Array.isArray(r.competencies) ? r.competencies : []).map((c) => {
    const score = Number.isFinite(c.score) ? c.score : Math.round(((c.level || 0) / 4) * 100);
    const qa = answerFor(c.competency_id);
    return {
      id: c.competency_id,
      name: c.scenario || c.competency_id || "Competency",
      score,
      level: c.level ?? null,
      band: c.band || bandFor(score),
      weight: c.weight || 1,
      justification: c.justification || "",
      evidence: (c.evidence_quotes || []).filter(Boolean),
      question: qa.question,
      answer: qa.answer,
    };
  });

  const status = r.scoring_status || (r.overall_percentage != null ? "scored" : "pending");
  const aiLevel = r.ai_likelihood_level || "";

  return {
    ...base,
    status, // scored | pending | review
    overall: status === "scored" ? r.overall_percentage ?? null : null,
    fit: r.fit || "",
    summary: r.ai_summary || "",
    engine: r.scoring_engine || "",
    competencies,
    strengths: competencies.filter((c) => c.band === "Strong").map((c) => c.name),
    gaps: competencies.filter((c) => c.band === "Developing").map((c) => c.name),
    probes: Array.isArray(r.interview_probes) ? r.interview_probes : [],
    probesStatus: r.interview_probes_status || null,
    benchmark: r.benchmark || null,
    signals: {
      pasted: integrity.pasted_attempts || 0,
      tabs: integrity.tab_blur_count || 0,
      awayMs: integrity.tab_blur_duration_ms || 0,
      fullscreen: integrity.fullscreen_exit_count || 0,
      devtools: integrity.devtools_detected_count || 0,
      rightClick: integrity.right_click_attempts || 0,
      fast: timings.filter((t) => t.flag === "implausibly_fast").length,
      aiLevel,
      aiSummary: aiLevel && aiLevel !== "insufficient" ? r.ai_likelihood_summary || "" : "",
      duplicates: (r.duplicate_flags || []).length,
      photo: Boolean(d?.photo_url),
    },
  };
}

export function benchmarkLine(benchmark) {
  if (!benchmark) return "";
  if (benchmark.available) {
    return `Scored higher than ${benchmark.percentile}% of ${benchmark.sample_size} candidates assessed for this role (role median ${benchmark.role_median}%).`;
  }
  const need = benchmark.min_sample || 30;
  return `A role benchmark appears once ${need} candidates have been assessed for this role (${benchmark.sample_size || 0} so far).`;
}

export function methodLine(report) {
  const n = report.competencies?.length || 0;
  return `Each written answer was scored 0–4 against an anchored rubric for its competency, using only evidence in the candidate's own words. The assessment score is the weighted average of ${n} competenc${n === 1 ? "y" : "ies"}, shown as 0–100%.`;
}
